/**
 * @format
 */
import AppPopup from '@/components/popups/app-popup';
import { ActivityTypesService } from '@/scripts/services/mdf/activity-types-service';
import { FundPoolsService } from '@/scripts/services/mdf/fund-pools-service';
import { MDFAgencyRequestService } from '@/scripts/services/mdf/agency/agency-request-service';
import { MDFPOPRequestService } from '@/scripts/services/mdf/agency/agency-pop-request-service';
import { AccountsService } from '@/scripts/services/mdf/accounts-service';
import { MDFAgencyAllocateRequestService } from '@/scripts/services/mdf/agency/agency-allocate-request-service';
import { MDFAgencyDenyRequestService } from '@/scripts/services/mdf/agency/agency-deny-request-service';
import { MDFAgencyManagersService } from '@/scripts/services/mdf/mdf-agency-managers-service';
import { MDFAgencyPreapprovedFundService } from '@/scripts/services/mdf/agency/agency-preapproved-fund-service';
import { v4 as uuidv4 } from 'uuid';

export const MDFAgencyRequestMixin = {
    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        avatarImage() {
            return require('@/assets/images/avatar.png');
        },
    },
    methods: {
        getActivityTypeOptions() {
            return new Promise((resolve, reject) => {
                ActivityTypesService.getAll(this.accountId)
                    .then(({ data }) => resolve(data.data.map(this.mapActivityTypesToOptions)))
                    .catch(reject);
            });
        },

        getFundPoolOptions() {
            const expiration = {
                $or: [
                    { ExpirationDate: { $gt: this.$moment().startOf('day').toISOString() } },
                    { ExpirationDate: { $eq: null } },
                ],
            };

            const availability = {
                AvailabilityDate: { $lte: this.$moment().startOf('day').toISOString() },
            };

            const expirationFilter = JSON.stringify(expiration);
            const availabilityFilter = JSON.stringify(availability);

            const config = `include=[{"model":"FundRequestAllocationModel", "required": false, "attributes": {"select":["AmountUSD"]}}]&where=[${expirationFilter},${availabilityFilter}]`;
            return new Promise((resolve, reject) => {
                FundPoolsService.getAll(this.accountId, config)
                    .then(({ data }) => resolve(data.data.map(this.mapFundPoolsToOptions)))
                    .catch(reject);
            });
        },

        getPreapprovedFunds(clientAccountId) {
            const expiration = {
                $or: [
                    { ExpirationDate: { $gt: this.$moment().startOf('day').toISOString() } },
                    { ExpirationDate: { $eq: null } },
                ],
            };

            const availability = {
                AvailabilityDate: { $lte: this.$moment().startOf('day').toISOString() },
            };

            const expirationFilter = JSON.stringify(expiration);
            const availabilityFilter = JSON.stringify(availability);
            const config = `include=[{"model":"FundPoolModel", "required": true,"attributes": {"select":["AvailableBalance","Id","Title","Budget","Type","AvailabilityDate","ExpirationDate"]},"where":[${expirationFilter},${availabilityFilter}]}]&attributes={"select":["RemainingBalanceUSD","Id","Name","RemainingBalance", "FundPoolId","PreapprovedAmountUSD"]}`;

            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.getClientPreapprovedFunds({
                    clientAccountId,
                    agencyAccountId: this.accountId,
                    config,
                })
                    .then(({ data: { data } }) => {
                        resolve(
                            data.data
                                .filter((fund) => fund.RemainingBalance)
                                .map(this.mapPreapprovedFundsToOptions),
                        );
                    })
                    .catch(reject);
            });
        },

        getPreapprovedFundsBalance(clientAccountId) {
            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.getBalance(this.accountId, clientAccountId)
                    .then(({ data }) => resolve(data.data))
                    .catch(reject);
            });
        },

        submitCreateRequest({ request, status, clientAccountId, funds }) {
            const payload = this.mapRequestDataToPayload({
                request,
                status,
                clientAccountId,
                funds,
            });
            return new Promise((resolve, reject) => {
                MDFAgencyRequestService.post(this.accountId, payload).then(resolve).catch(reject);
            });
        },

        submitCreatePreapprovedRequest({ request, clientAccountId, funds, formType }) {
            const payload = this.mapRequestDataToPayload({
                request,
                clientAccountId,
                funds,
                formType,
            });
            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.post(this.accountId, payload)
                    .then(resolve)
                    .catch(reject);
            });
        },

        submitEditPreapprovedRequest({
            request,
            status,
            clientAccountId,
            preApprovedFundId,
            funds,
            formType,
        }) {
            const payload = this.mapRequestDataToPayload({
                request,
                status,
                clientAccountId,
                funds,
                formType,
            });
            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.patch(this.accountId, preApprovedFundId, payload)
                    .then(resolve)
                    .catch(reject);
            });
        },

        getPreApprovedFund(preApprovedFundId) {
            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.getPreApproved({
                    agencyAccountId: this.accountId,
                    preApprovedFundId,
                })
                    .then(({ data }) => resolve(data.data))
                    .catch(reject);
            });
        },

        getClientData(accountId) {
            return new Promise((resolve, reject) => {
                AccountsService.getClient(accountId)
                    .then(({ data }) => resolve(data.data))
                    .catch(reject);
            });
        },

        async getRequestDetails({ fundRequestId, detailsParser, isPopReview }) {
            try {
                const { data } = await MDFAgencyRequestService.getById(
                    this.accountId,
                    fundRequestId,
                );

                const {
                    RequestedAmountSelectedCurrency,
                    TotalAmountSelectedCurrency,
                    PreapprovedFundsSelectedCurrency,
                    CurrencySymbol,
                    CurrencyCode,
                    ClientAccountId,
                } = data.data;

                const requestDetails = {
                    clientAccountId: ClientAccountId,
                    requestDetails: detailsParser(data.data),
                    requestAmounts: {
                        preapprovedAmount: PreapprovedFundsSelectedCurrency,
                        requestedAmount: RequestedAmountSelectedCurrency,
                        totalActivityAmount: TotalAmountSelectedCurrency,
                        currencySymbol: CurrencySymbol,
                        currencyCode: CurrencyCode,
                    },
                };

                if (isPopReview && data.data.Allocations.length) {
                    const reviewPopSubmission = await this.parseAllocationToReviewSubmission(
                        data.data.Allocations,
                    );
                    requestDetails.reviewPopSubmission = reviewPopSubmission;
                }

                const prefillAllocationData = this.parseAllocationToPrefillData(data.data);
                requestDetails.prefillAllocationData = prefillAllocationData;

                if (data.data.AllocatedAmountUSDHistorical) {
                    requestDetails.allocatedFunds = data.data.AllocatedAmountUSDHistorical;
                }

                return requestDetails;
            } catch (error) {
                return Promise.reject(error);
            }
        },

        postProofOfPerformance(fundRequestId, payload) {
            return new Promise((resolve, reject) => {
                MDFPOPRequestService.post(this.accountId, fundRequestId, payload)
                    .then(({ data }) => {
                        resolve(data);
                    })
                    .catch(reject);
            });
        },

        getFundPoolById(fundPoolId) {
            return new Promise((resolve, reject) => {
                FundPoolsService.getById(fundPoolId, this.accountId)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject);
            });
        },

        getPreapprovedFundPoolById(preapprovedFundId, clientId) {
            return new Promise((resolve, reject) => {
                MDFAgencyPreapprovedFundService.getById(this.accountId, preapprovedFundId, clientId)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject);
            });
        },

        submitAllocateFund({ request, fundRequestId, funds }, isEdit) {
            const payload = this.parseAllocationsRequestToPayload(request, funds, fundRequestId);
            if (isEdit) {
                return new Promise((resolve, reject) => {
                    MDFAgencyAllocateRequestService.put(this.accountId, fundRequestId, payload)
                        .then(({ data }) => {
                            resolve(data.data);
                        })
                        .catch(reject);
                });
            }
            return new Promise((resolve, reject) => {
                MDFAgencyAllocateRequestService.post(this.accountId, fundRequestId, payload)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject);
            });
        },

        postDenyFund({ request, fundRequestId }) {
            const payload = this.parseAllocationDenyRequestToPayload(request, fundRequestId);

            return new Promise((resolve, reject) => {
                MDFAgencyDenyRequestService.post(this.accountId, fundRequestId, payload)
                    .then(({ data }) => {
                        resolve(data.data);
                    })
                    .catch(reject);
            });
        },

        async getWidgetRequestDetails(
            accountId,
            fundRequestId,
            isClient,
            vendor,
            requestMadeBy,
            clientId,
        ) {
            try {
                const { data } = await MDFAgencyRequestService.getById(accountId, fundRequestId);
                const showFundPool = !!data.data.Allocations.length;

                if (showFundPool) {
                    const reviewPopSubmission = await this.parseAllocationToReviewSubmission(
                        data.data.Allocations,
                        clientId,
                    );

                    return this.parseMDFRequestToDetailsWidget(
                        data.data,
                        reviewPopSubmission,
                        isClient,
                        vendor,
                        requestMadeBy,
                    );
                } else {
                    return this.parseMDFRequestToDetailsWidget(
                        data.data,
                        null,
                        isClient,
                        vendor,
                        requestMadeBy,
                    );
                }
            } catch (error) {
                return Promise.reject(error);
            }
        },

        dispatchGetManagersPrefill(clientAccountId) {
            const config = `where=[{"$ClientAccountId$": ${clientAccountId}}]`;
            return new Promise((resolve, reject) => {
                MDFAgencyManagersService.getAll({ accountId: this.accountId, config })
                    .then(({ data: { data } }) => {
                        resolve({
                            ids: data.map(({ ParticipantId }) => ParticipantId),
                        });
                    })
                    .catch(reject);
            });
        },

        // Mappers
        mapActivityTypesToOptions(activity) {
            const { Id, Type, Proofs } = activity;

            return {
                id: Id,
                name: Type,
                pop: Proofs.map(({ Name }) => Name),
            };
        },

        mapFundPoolsToOptions(fundPool) {
            const { Id, Title, Budget, ExpirationDate, AvailableBalance, Type } = fundPool;

            return {
                id: Id,
                name: Title,
                expiration: ExpirationDate && this.$moment(ExpirationDate).local(),
                budget: Budget,
                availableBalance: AvailableBalance,
                type: Type,
            };
        },

        mapPreapprovedFundsToOptions(fundPool) {
            const { Id, Name, FundPool, RemainingBalanceUSD } = fundPool;
            return {
                id: Id,
                fundPoolId: FundPool.Id,
                budgetName: FundPool.Title,
                name: Name,
                availablePreapprovedFund: RemainingBalanceUSD,
                expiration:
                    FundPool.ExpirationDate && this.$moment(FundPool.ExpirationDate).local(),
                budget: FundPool.Budget,
                availableBalance: FundPool.AvailableBalance,
                type: FundPool.Type,
            };
        },

        mapUserToOption(user) {
            return {
                id: user.Id,
                name: this.$helpers.getName(user.FirstName, user.LastName),
                profilePicture: user.ProfileImageUrl,
                title: user.Title,
            };
        },

        mapPreApproveData(response) {
            const contactsData = response.Participants.filter(
                (contact) => contact.ParticipantType === 'Contact',
            ).map((contact) => ({
                id: contact.ParticipantId,
                name: contact.Name,
                profilePicture: contact.ProfileImageUrl,
            }));

            const managersData = response.Participants.filter(
                (contact) => contact.ParticipantType === 'Manager',
            ).map((contact) => ({
                id: contact.ParticipantId,
                name: contact.Name,
                profilePicture: contact.ProfileImageUrl,
            }));

            const approversData = response.Participants.filter(
                (contact) => contact.ParticipantType === 'Approver',
            ).map((contact) => ({
                id: contact.ParticipantId,
                name: contact.Name,
                profilePicture: contact.ProfileImageUrl,
            }));

            const allocatedFundsData = {
                amount: response.PreapprovedAmountSelectedCurrency,
                requestedAmount: response.RequestedAmountSelectedCurrency,
                fundPool: {
                    availableBalance: response.FundPool.AvailableBalance,
                    budget: response.FundPool.Budget,
                    id: response.FundPool.Id,
                    expiration:
                        response.FundPool.ExpirationDate &&
                        this.$moment(response.FundPool.ExpirationDate).local(),
                    name: response.FundPool.Title,
                    type: response.FundPool.Type,
                },

                popData: {
                    pop: [],
                    isPopEnabled: true,
                },

                id: response.FundPool.Id,
            };

            return {
                contactsData,
                managersData,
                approversData,
                allocatedFundsData,
                fundTitle: response.Name || null,
                notes: response.NotesForPartner,
            };
        },

        mapRequestDataToPayload({ request, status, clientAccountId, funds, formType }) {
            const { contactInfo, allocatedFunds, dateData, managers, approvers, notes } = request;

            const { fundTitle, contacts, activityType } = contactInfo;

            const { allocation, expiration, approved } = dateData;

            const participants = managers
                .map(({ name, profilePicture, id }) => ({
                    ParticipantId: id,
                    ParticipantType: 'Manager',
                    Name: name,
                    ProfileImageUrl: profilePicture,
                }))
                .concat(
                    approvers.map(({ name, profilePicture, id }) => ({
                        ParticipantId: id,
                        ParticipantType: 'Approver',
                        Name: name,
                        ProfileImageUrl: profilePicture,
                    })),
                )
                .concat(
                    contacts.map(({ name, profilePicture, id }) => ({
                        ParticipantId: id,
                        ParticipantType: 'Contact',
                        Name: name,
                        ProfileImageUrl: profilePicture,
                    })),
                );

            const allocations = allocatedFunds.map(
                ({
                    isPreapprovedFundEnable,
                    preapprovedFund,
                    fundPool,
                    amount,
                    purpose,
                    popData,
                }) => {
                    return {
                        FundPoolId: isPreapprovedFundEnable
                            ? preapprovedFund.fundPoolId
                            : fundPool.id,
                        ...(isPreapprovedFundEnable && { PreapproveFundId: preapprovedFund.id }),
                        AmountUSD: Number(amount),
                        Name: purpose,
                        ProofRequired: popData.isPopEnabled,
                        ProofTypes: popData.pop,
                    };
                },
            );

            let payload = {};

            if (formType === 'preapprove' || formType === 'preapproveAndEdit') {
                const { fundPool } = allocatedFunds[0];
                payload = {
                    Name: fundTitle,
                    ClientAccountId: Number(clientAccountId),
                    PreapprovedAmountSelectedCurrency: funds.amount,
                    CurrencyCode: 'USD',
                    CurrencySymbol: funds.currencySymbol,
                    Participants: participants,
                    FundPoolId: fundPool.id,
                    NotesForPartner: notes,
                };

                return payload;
            }

            payload = {
                Name: fundTitle,
                Status: status,
                ClientAccountId: Number(clientAccountId),
                AgencyAccountId: Number(this.accountId),
                ActivityType: activityType ? activityType.name : null,
                TotalAmountSelectedCurrency: funds.amount,
                RequestedAmountSelectedCurrency: funds.amount,
                AmountUSD: funds.amount,
                CurrencyCode: 'USD',
                CurrencySymbol: funds.currencySymbol,
                CurrencyExchangeRate: 1000,
                Participants: participants,
                ActivityDate: this.$moment().startOf('day').toISOString(),
                Allocations: allocations,
                ApprovedDate: approved && this.$moment(approved).startOf('day').toISOString(),
            };

            if (allocation && expiration) {
                payload.AvailabilityDate = this.$moment(allocation).startOf('day').toISOString();
                payload.ExpirationDate = this.$moment(expiration).startOf('day').toISOString();
            }

            if (notes) {
                payload.AllocationNotes = notes;
            }

            return payload;
        },

        parseParticipantsToContacts(participants) {
            return participants
                .filter((participant) => participant.ParticipantType === 'Contact')
                .map((participant) => ({
                    data: participant.Name,
                    imgSrc: participant.ProfileImageUrl,
                    showContactImage: true,
                    id: participant.ParticipantId,
                }));
        },

        parseMDFRequestToPOPRequestDetails(mdfRequest) {
            const {
                Name,
                ClientName,
                ClientLogoUrl,
                RequestedAmountSelectedCurrency,
                TargetAudience,
                ActivityType,
                CreatedAt,
                TotalAmountSelectedCurrency,
                ExpectedAttendance,
                EstimatedNumberOfLeads,
                ProjectedRoi,
                ActivityDate,
                CurrencyCode,
                CurrencySymbol,
                Participants,
                RequestNotes,
            } = mdfRequest;

            const requestDetails = [
                {
                    title: 'Fund Title',
                    content: {
                        data: Name,
                    },
                },
                {
                    title: 'Company Name',
                    content: {
                        data: ClientName,
                        imgSrc: ClientLogoUrl,
                        showCompanyLogo: ClientLogoUrl !== '',
                    },
                },
                {
                    title: 'Contact',
                    content: this.parseParticipantsToContacts(Participants),
                },
                {
                    title: 'Requested Amount',
                    content: {
                        data: this.formatCurrencyWithCode(
                            RequestedAmountSelectedCurrency,
                            CurrencySymbol,
                            CurrencyCode,
                        ),
                    },
                },
                {
                    title: 'Total Cost of Activity',
                    content: {
                        data: this.formatCurrencyWithCode(
                            TotalAmountSelectedCurrency,
                            CurrencySymbol,
                            CurrencyCode,
                        ),
                    },
                },
                {
                    title: 'Request Created on:',
                    content: {
                        data: this.formatDate(CreatedAt),
                    },
                },
                {
                    title: 'Request Type',
                    content: {
                        data: ActivityType || 'N/A',
                    },
                },
                {
                    title: 'Target Audience',
                    content: {
                        data: TargetAudience || 'N/A',
                    },
                },
                {
                    title: 'Expected Number of Attendees',
                    content: {
                        data: ExpectedAttendance || 'N/A',
                    },
                },
                {
                    title: 'Estimated Number of Leads',
                    content: {
                        data: EstimatedNumberOfLeads || 'N/A',
                    },
                },
                {
                    title: 'Projected Return on Investment',
                    content: {
                        data: ProjectedRoi
                            ? this.formatCurrencyWithCode(
                                  ProjectedRoi,
                                  CurrencySymbol,
                                  CurrencyCode,
                              )
                            : 'N/A',
                    },
                },
                {
                    title: 'Date of activity:',
                    content: {
                        data: this.formatDate(ActivityDate),
                    },
                },
                {
                    title: 'Additional Info',
                    content: {
                        data: RequestNotes || 'N/A',
                    },
                },
            ];

            return requestDetails;
        },

        parseMDFRequestToAllocateRequestDetails(mdfRequest) {
            const {
                Name,
                ClientName,
                ClientLogoUrl,
                TargetAudience,
                ActivityType,
                CreatedAt,
                ExpectedAttendance,
                EstimatedNumberOfLeads,
                ProjectedRoi,
                ActivityDate,
                CurrencySymbol,
                Participants,
                RequestNotes,
            } = mdfRequest;

            const requestDetails = [
                {
                    title: 'Fund Title',
                    content: {
                        data: Name,
                    },
                },
                {
                    title: 'Company Name',
                    content: {
                        data: ClientName,
                        imgSrc: ClientLogoUrl,
                        showCompanyLogo: ClientLogoUrl !== '',
                    },
                },
                {
                    title: 'Contact',
                    content: this.parseParticipantsToContacts(Participants),
                },
                {
                    title: 'Request Created on:',
                    content: {
                        data: this.formatDate(CreatedAt),
                    },
                },
                {
                    title: 'Activity Type',
                    content: {
                        data: ActivityType || 'N/A',
                    },
                },
                {
                    title: 'Target Audience',
                    content: {
                        data: TargetAudience || 'N/A',
                    },
                },
                {
                    title: 'Expected Number of Attendees',
                    content: {
                        data: ExpectedAttendance || 'N/A',
                    },
                },
                {
                    title: 'Estimated Number of Leads',
                    content: {
                        data: EstimatedNumberOfLeads || 'N/A',
                    },
                },
                {
                    title: 'Projected Return on Investment',
                    content: {
                        data: ProjectedRoi
                            ? this.formatCurrency(ProjectedRoi, CurrencySymbol)
                            : 'N/A',
                    },
                },
                {
                    title: 'Date of activity:',
                    content: {
                        data: this.formatDate(ActivityDate),
                    },
                },
                {
                    title: 'Additional Info',
                    content: {
                        data: RequestNotes || 'N/A',
                    },
                },
            ];

            return requestDetails;
        },

        formatUploadDates(dates) {
            return dates
                .map(({ CreatedAt }) => this.$moment(CreatedAt).local().format('MMM D, YYYY'))
                .reduce((accumulator, currentValue) => {
                    if (!accumulator.includes(currentValue)) {
                        accumulator.push(currentValue);
                    }
                    return accumulator;
                }, []);
        },

        mapFilesToReview({ Type, Name, Size, Url, Id }) {
            return {
                id: Id,
                type: Type,
                name: Name,
                size: Size,
                downloadUrl: Url,
            };
        },

        async parseAllocationToReviewSubmission(allocations, clientId) {
            const reviewSubmissions = await Promise.all(
                allocations.map(
                    async ({
                        Id,
                        Status,
                        Name,
                        AmountUSD,
                        ProofTypes,
                        ProofRequired,
                        FundPoolId,
                        FundAllocationPops,
                        NotesForVendor,
                        PopSubmittedByUser,
                        PreapproveFundId,
                    }) => {
                        const fundPool = await this.getFundPoolById(FundPoolId);
                        let preapprovedFund = null;

                        if (PreapproveFundId) {
                            preapprovedFund = await this.getPreapprovedFundPoolById(
                                PreapproveFundId,
                                clientId,
                            );
                        }

                        const fundPoolName = fundPool.Title;
                        const fundPoolType = fundPool.Type;
                        const fundPoolExpiration = fundPool.ExpirationDate;
                        const fundPoolId = fundPool.Id;

                        const uploads = FundAllocationPops.filter(
                            ({ IsResubmitted }) => !IsResubmitted,
                        ).map(this.mapFilesToReview);

                        const uploadDates = this.formatUploadDates(
                            FundAllocationPops.filter(({ IsResubmitted }) => !IsResubmitted),
                        );

                        const uploadDate = uploadDates.join(' / ');

                        const reuploads = FundAllocationPops.filter(
                            ({ IsResubmitted }) => IsResubmitted,
                        ).map(this.mapFilesToReview);

                        const reUploadDates = this.formatUploadDates(
                            FundAllocationPops.filter(({ IsResubmitted }) => IsResubmitted),
                        );

                        const reuploadDate = reUploadDates.join(' / ');

                        let state = null;

                        switch (Status) {
                            case 'Approved':
                                state = 'accept';
                                break;
                            case 'Denied':
                                state = 'deny';
                                break;

                            default:
                                break;
                        }

                        return {
                            id: Id,
                            state,
                            acceptNotes: null,
                            denyNotes: null,
                            clientNotes: NotesForVendor && {
                                content: NotesForVendor,
                                from: {
                                    name: `${PopSubmittedByUser.FirstName} ${PopSubmittedByUser.LastName}`,
                                    profilePicture: PopSubmittedByUser.ProfileImageUrl,
                                },
                            },
                            purpose: {
                                label: 'Allocated Funds',
                                data: Name,
                            },
                            amount: AmountUSD,
                            fundPool: {
                                id: fundPoolId,
                                name: fundPoolName,
                                type: fundPoolType,
                                expiration:
                                    fundPoolExpiration && this.$moment(fundPoolExpiration).local(),
                            },
                            ...(preapprovedFund && {
                                isPreapprovedFundEnable: PreapproveFundId ? true : false,
                                preapprovedFund: {
                                    name: preapprovedFund.Name,
                                    availablePreapprovedFund: preapprovedFund.RemainingBalanceUSD,
                                },
                            }),
                            popData: {
                                isPopEnabled: ProofRequired,
                                pop: ProofTypes,
                            },
                            documents: {
                                uploads,
                                uploadDate,
                                reuploads,
                                reuploadDate,
                            },
                        };
                    },
                ),
            );

            return reviewSubmissions;
        },

        parseAllocationToPrefillData(request) {
            const {
                Allocations,
                AvailabilityDate,
                ExpirationDate,
                Participants,
                AllocationNotes,
                ReasonForDenial,
                Status,
                ActivityType,
                ClientAccountId,
            } = request;

            const managerIds = Participants.filter(
                ({ ParticipantType }) => ParticipantType === 'Manager',
            ).map(({ ParticipantId }) => ParticipantId);
            const approverIds = Participants.filter(
                ({ ParticipantType }) => ParticipantType === 'Approver',
            ).map(({ ParticipantId }) => ParticipantId);

            return {
                clientAccountId: ClientAccountId,
                activityType: ActivityType,
                dateData: {
                    allocation: AvailabilityDate && this.$moment(AvailabilityDate).toDate(),
                    expiration: ExpirationDate && this.$moment(ExpirationDate).toDate(),
                    approved: null,
                },

                managers: managerIds,
                approvers: approverIds,
                notes: Status === 'Denied' ? ReasonForDenial : AllocationNotes,
                allocatedFunds: Allocations.map(
                    ({
                        Id,
                        Name,
                        AmountUSD,
                        ProofTypes,
                        FundPoolId,
                        ProofRequired,
                        PreapproveFundId,
                    }) => {
                        return {
                            id: Id,
                            purpose: Name,
                            amount: AmountUSD,
                            fundPool: { id: FundPoolId || null },
                            isPreapprovedFundEnable: PreapproveFundId ? true : false,
                            preapprovedFund: {
                                id: PreapproveFundId || null,
                                name: null,
                                expiration: null,
                                type: null,
                            },
                            popData: {
                                pop: ProofTypes,
                                isPopEnabled: ProofRequired,
                            },
                        };
                    },
                ),
            };
        },

        parseParticipantsToMultiProfile(participants, participantType) {
            return participants
                .filter((participant) => participant.ParticipantType === participantType)
                .map((participant) => ({
                    profilePicture: participant.ProfileImageUrl || this.avatarImage,
                    name: participant.Name,
                    title: '',
                    id: participants.ParticipantId,
                }));
        },

        parseRequestMadeByToMultiProfile({ firstName, profileImageUrl }) {
            return [
                {
                    profilePicture: profileImageUrl || this.avatarImage,
                    name: firstName,
                    title: '',
                    id: uuidv4(),
                },
            ];
        },

        parseMDFRequestToDetailsWidget(mdfRequest, fundPools, isClient, vendor, requestMadeBy) {
            const {
                Name,
                ClientName,
                ClientLogoUrl,
                RequestedAmountSelectedCurrency,
                TargetAudience,
                ActivityType,
                CreatedAt,
                TotalAmountSelectedCurrency,
                ExpectedAttendance,
                EstimatedNumberOfLeads,
                ProjectedRoi,
                ActivityDate,
                CurrencyCode,
                CurrencySymbol,
                Participants,
                AllocatedAmountUSDHistorical,
                ApprovedFundsUSD,
                PreapprovedFundsSelectedCurrency,
                ReasonForDenial,
                Status,
                DeniedDate,
                ExpirationDate,
                PopSubmittedDate,
                ApprovedDate,
                PaidDate,
                AvailabilityDate,
                PopDeniedDate,
                RequestNotes,
                Allocations,
            } = mdfRequest;

            const fundPoolName =
                fundPools &&
                fundPools
                    .filter(
                        ({ fundPool }, idx, arr) =>
                            arr.findIndex(({ fundPool: { id } }) => id === fundPool.id) === idx,
                    )
                    .map(({ fundPool: { name } }) => name)
                    .join(', ');

            const requestDetails = [
                {
                    label: 'Vendor',
                    content: isClient && {
                        profileImageUrl: vendor.logoUrl,
                        name: vendor.name,
                    },
                    contentType: 'profile',
                },
                {
                    label: 'Company Name',
                    content: !isClient && {
                        profileImageUrl: ClientLogoUrl,
                        name: ClientName,
                    },
                    contentType: 'profile',
                },
                {
                    label: 'Request made by:',
                    content: isClient && this.parseRequestMadeByToMultiProfile(requestMadeBy),
                    contentType: 'multiProfile',
                },
                {
                    label: 'Contact',
                    content:
                        !isClient && this.parseParticipantsToMultiProfile(Participants, 'Contact'),
                    contentType: 'multiProfile',
                },
                {
                    label: 'Request Created on:',
                    content: this.$moment(CreatedAt).local(),
                    contentType: 'date',
                },
                {
                    label: 'Status',
                    content: Status,
                    contentType: 'chip',
                },
                {
                    label: 'Activity Type',
                    content: ActivityType && ActivityType,
                    contentType: 'text',
                },
                {
                    label: 'Which fund pool will this come from?',
                    content: !isClient && fundPoolName && fundPoolName,
                    contentType: 'text',
                },
                {
                    label: 'Date of activity:',
                    content: this.$moment(ActivityDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Expected Number of Attendees',
                    content: ExpectedAttendance,
                    contentType: 'number',
                },
                {
                    label: 'Target Audience',
                    content: TargetAudience,
                    contentType: 'text',
                },
                {
                    label: 'Estimated Number of Leads',
                    content: EstimatedNumberOfLeads,
                    contentType: 'number',
                },
                {
                    label: 'Projected Return on Investment',
                    content: ProjectedRoi && {
                        currencyCode: CurrencyCode,
                        amount: ProjectedRoi,
                        currencySymbol: CurrencySymbol,
                    },
                    contentType: 'currency',
                },
                {
                    label: 'Fund Allocation Date',
                    content: AvailabilityDate && this.$moment(AvailabilityDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Date Request Denied',
                    content: DeniedDate && this.$moment(DeniedDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Fund Expiration Date',
                    content: ExpirationDate && this.$moment(ExpirationDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Date POP Submitted',
                    content: PopSubmittedDate && this.$moment(PopSubmittedDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Date POP Denied',
                    content: PopDeniedDate && this.$moment(PopDeniedDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Funds Approved Date',
                    content: !isClient && ApprovedDate && this.$moment(ApprovedDate).local(),
                    contentType: 'date',
                },
                {
                    label: isClient ? 'Funds Given Date' : 'Funds Paid Date',
                    content: PaidDate && this.$moment(PaidDate).local(),
                    contentType: 'date',
                },
                {
                    label: 'Account Manager',
                    content: this.parseParticipantsToMultiProfile(Participants, 'Manager'),
                    contentType: 'multiProfile',
                },
                {
                    label: 'Funds Approver',
                    content: this.parseParticipantsToMultiProfile(Participants, 'Approver'),
                    contentType: 'multiProfile',
                },
                {
                    label: 'Reason for Denial',
                    content: ReasonForDenial && ReasonForDenial,
                    contentType: 'text',
                },
                {
                    label: 'Additional Info',
                    content: RequestNotes,
                    contentType: 'text',
                },
            ];

            var allocations = Allocations.map((allocation) => {
                return {
                    cardDetails: {
                        allocatedFundName: allocation.Name,
                        fundAmount: {
                            currencyCode: CurrencyCode,
                            currency: CurrencySymbol,
                            amount: allocation.AmountSelectedCurrency,
                        },
                        status: allocation.Status,
                        notesForClient: allocation.NotesForClient,
                        documentPOPNeeded: allocation.ProofTypes,
                        documentFiles: this.mapDocumentFiles(allocation.FundAllocationPops),
                    },
                };
            });

            const mdfRequestMainDetails = {
                title: Name,
                totalCostOfActivity: {
                    currencyCode: CurrencyCode,
                    currencySymbol: CurrencySymbol,
                    amount: TotalAmountSelectedCurrency,
                },
                requestedAmount: {
                    currencyCode: CurrencyCode,
                    currencySymbol: CurrencySymbol,
                    amount: RequestedAmountSelectedCurrency,
                },
                allocatedAmount: {
                    currencyCode: 'USD',
                    currencySymbol: '$',
                    amount: AllocatedAmountUSDHistorical,
                },
                allocations: allocations,
                fundsApproved: ApprovedFundsUSD
                    ? {
                          currencySymbol: '$',
                          amount: ApprovedFundsUSD,
                      }
                    : null,
                preapprovedFunds: PreapprovedFundsSelectedCurrency
                    ? {
                          currencySymbol: '$',
                          amount: PreapprovedFundsSelectedCurrency,
                      }
                    : null,
                status: Status,
            };

            const detailsWidget = {
                requestDetails,
                mdfRequestMainDetails,
            };

            return detailsWidget;
        },

        parsePOPAllocationsToPayload(allocations) {
            const parsedAllocations = allocations.map(({ id, acceptNotes, denyNotes, state }) => {
                const notesForClient = state.value === 'accept' ? acceptNotes : denyNotes;

                let status;
                if (state.value === 'accept') {
                    status = 'Approved';
                } else if (state.value === 'deny') {
                    status = 'Denied';
                } else {
                    status = undefined;
                }

                const payload = {
                    Id: id,
                    Status: status,
                };

                if (notesForClient) {
                    payload.NotesForClient = notesForClient;
                }
                return payload;
            });

            return {
                Allocations: parsedAllocations,
            };
        },

        parseAllocationsRequestToPayload(request, funds, fundRequestId) {
            const { allocatedFunds, dateData, managers, approvers, contacts, notes } = request;

            const { allocation, expiration } = dateData;

            const participants = managers
                .map(({ name, profilePicture, id }) => ({
                    ParticipantId: id,
                    ParticipantType: 'Manager',
                    Name: name,
                    ProfileImageUrl: profilePicture,
                }))
                .concat(
                    approvers.map(({ name, profilePicture, id }) => ({
                        ParticipantId: id,
                        ParticipantType: 'Approver',
                        Name: name,
                        ProfileImageUrl: profilePicture,
                    })),
                )
                .concat(
                    contacts.map(({ data, imgSrc, id }) => ({
                        ParticipantId: id,
                        ParticipantType: 'Contact',
                        Name: data,
                        ProfileImageUrl: imgSrc,
                    })),
                );

            const allocations = allocatedFunds.map(
                ({
                    fundPool,
                    amount,
                    purpose,
                    popData,
                    id,
                    isPreapprovedFundEnable,
                    preapprovedFund,
                }) => {
                    const allocationPayload = {
                        FundPoolId: isPreapprovedFundEnable
                            ? preapprovedFund.fundPoolId
                            : fundPool.id,
                        ...(isPreapprovedFundEnable && { PreapproveFundId: preapprovedFund.id }),
                        AmountUSD: Number(amount),
                        Name: purpose,
                        ProofRequired: popData.isPopEnabled,
                        ProofTypes: popData.pop,
                        FundRequestId: Number(fundRequestId),
                    };

                    if (Number(id)) {
                        allocationPayload.Id = Number(id);
                    }

                    return allocationPayload;
                },
            );

            const payload = {
                AmountUSD: funds.amount,
                AvailabilityDate: this.$moment(allocation).startOf('day').toISOString(),
                ExpirationDate: this.$moment(expiration).startOf('day').toISOString(),
                Participants: participants,
                Allocations: allocations,
            };

            if (notes) {
                payload.AllocationNotes = notes;
            }

            return payload;
        },

        parseAllocationDenyRequestToPayload(request, fundRequestId) {
            const { managers, approvers, notes } = request;

            const participants = managers
                .map(({ name, profilePicture, id }) => ({
                    ParticipantId: id,
                    ParticipantType: 'Manager',
                    Name: name,
                    ProfileImageUrl: profilePicture,
                    FundRequestId: fundRequestId,
                }))
                .concat(
                    approvers.map(({ name, profilePicture, id }) => ({
                        ParticipantId: id,
                        ParticipantType: 'Approver',
                        Name: name,
                        ProfileImageUrl: profilePicture,
                        FundRequestId: fundRequestId,
                    })),
                );

            return {
                ReasonForDenial: notes,
                Participants: participants,
            };
        },

        // Helpers
        formatDate(date) {
            if (!date) {
                return '';
            }

            return `${this.$moment(date).local().format('ddd').toUpperCase()} | ${this.$moment(date)
                .local()
                .format('MMM Do, YYYY')
                .toUpperCase()}`;
        },

        formatCurrency(amount, currencySymbol) {
            var parts = amount.toString().split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            const commaDelimitedAmount = parts.join('.');
            const formattedAmount = `${currencySymbol}${commaDelimitedAmount}`;

            return formattedAmount;
        },

        formatCurrencyWithCode(amount, currencySymbol, currencyCode) {
            const formattedAmount = this.formatCurrency(amount, currencySymbol);
            return `${currencyCode} ${formattedAmount}`;
        },

        // Factories
        createSubmitFormModal(title, content, type = 'success-popup success') {
            return () => {
                this.$modal.show(
                    AppPopup,
                    {
                        type,
                        title,
                        content,
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    },
                );
            };
        },

        mapDocumentFiles(Files) {
            return Files.map((file) => ({
                name: file.Name,
                url: file.Url,
                size: file.Size,
                type: file.Type,
                fileId: file.FileId,
                id: file.Id,
                downloadUrl: file.Url,
            }));
        },
    },
};
